<template>
  <div class="space-y-6">
    <BaseCard
      v-if="newArt"
      class="overflow-hidden"
      padding="p-0"
      :border="'border-none'"
    >
      <div class="space-y-6">
        <!-- Artwork gallery -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <BaseImage
            v-for="(img, i) in galleryItems"
            :key="i"
            @contextmenu.prevent
            @dragstart.prevent
            :uri="img"
            alt="Medium.am new artwork"
            img-class="w-full h-40 sm:h-60 object-cover rounded-lg transition-all duration-300 hover:object-contain"
          />
        </div>

        <!-- Artwork title -->
        <div class="px-4 sm:px-6 space-y-1">
          <h2 class="text-2xl font-bold leading-tight">
            {{ newArt.title.en }}
          </h2>
          <p class="text-sm text-gray-500">
            {{ newArt.title.hy }} / {{ newArt.title.ru }}
          </p>
        </div>

        <div class="px-4 sm:px-6 space-y-6">
          <!-- Artwork details -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div class="space-y-5">
              <div class="space-y-1">
                <p class="text-sm text-gray-500">
                  {{ $t("new.art_type") }}
                </p>
                <p class="text-[15px]">
                  {{ getArtTypeLocale(newArt.artType) }}
                </p>
              </div>

              <div class="space-y-1">
                <p class="text-sm text-gray-500">
                  {{ $t("new.medium") }}
                </p>
                <div class="flex flex-wrap gap-x-1">
                  <span
                    class="text-[15px]"
                    v-for="(m, index) in newArt.mediums"
                    :key="index"
                  >
                    {{ m.label[locale as Locale]
                    }}{{ index < newArt.mediums.length - 1 ? "," : "" }}
                  </span>
                </div>
              </div>

              <div v-if="newArt.creationYear" class="space-y-1">
                <p class="text-sm text-gray-500">
                  {{ $t("new.year_creation") }}
                </p>
                <p class="text-[15px]">{{ newArt.creationYear }}</p>
              </div>
            </div>

            <div class="space-y-5">
              <div class="space-y-1">
                <p class="text-sm text-gray-500">
                  {{ $t("new.dimensions") }}
                </p>
                <p class="text-[15px]">
                  {{ newArt.dimensions.height }} ×
                  {{ newArt.dimensions.width }}
                  <span v-if="newArt.dimensions.depth">
                    × {{ newArt.dimensions.depth }}
                  </span>
                  {{ newArt.dimensions.unit }}
                </p>
              </div>

              <div v-if="newArt.location" class="space-y-3">
                <p class="text-sm text-gray-500">
                  {{ $t("new.location") }}
                </p>

                <div v-if="newArt.location.countryCode" class="space-y-1">
                  <p class="text-sm text-gray-500">
                    {{ $t("new.country_label") }}
                  </p>
                  <p>
                    {{ getCountryLabel(newArt.location.countryCode) }}
                  </p>
                </div>

                <div v-if="newArt.location.city" class="space-y-1">
                  <p class="text-sm text-gray-500">
                    {{ $t("new.city_label") }}
                  </p>
                  <p class="text-[15px]">
                    {{
                      newArt.location.city[locale as Locale] ||
                      $t("provile.profile")
                    }}
                  </p>
                </div>

                <div v-if="newArt.location.venue" class="space-y-1">
                  <p class="text-sm text-gray-500">
                    {{ newArt.location.venue.type }}
                  </p>
                  <p class="text-[15px]">
                    {{
                      cutName(
                        newArt.location.venue.title || $t("provile.profile"),
                        40,
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div
            v-if="
              newArt.description.en ||
              newArt.description.hy ||
              newArt.description.ru
            "
            class="space-y-2"
          >
            <p class="text-sm text-gray-500">
              {{ $t("new.description") }}
            </p>
            <p class="text-[15px] leading-relaxed whitespace-pre-line">
              {{
                newArt.description[locale] ||
                newArt.description.hy ||
                newArt.description.en ||
                newArt.description.ru ||
                $t("new.no_description")
              }}
            </p>
          </div>
        </div>

        <!-- Edit action -->
        <div class="flex justify-end border-t border-gray-100 p-4 sm:px-6">
          <BaseCircleButton @click="store.setStep(0)">
            <Icon name="tabler:pencil" size="20" />
          </BaseCircleButton>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { ArtType, ArtTypeItem, NewArtwork } from "~~/types/artwork";
import { useI18n } from "vue-i18n";
import type { Locale } from "~~/types/global";
import { artTypes } from "~~/types/artwork";
import { cutName } from "../../utils/helpers";
import { COUNTRIES } from "@/data/countries";

const store = useNewArtworkStore();
const newArt = computed<NewArtwork | null>(() => store.newArtwork);
const { locale } = useI18n();

const getArtTypeLocale = (art_typ: ArtType): string => {
  // 1. Find the item
  const found = artTypes.find((item: ArtTypeItem) => item.key === art_typ);

  // 2. Safely access the locale with a fallback to the key itself
  // We cast locale.value as keyof typeof found.label to satisfy TS
  return found?.label[locale.value as keyof typeof found.label] || art_typ;
};

const getCountryLabel = (code: string): string => {
  const label = COUNTRIES.find((c) => c.code === code)?.label || {
    en: "Armenia",
    ru: "Армения",
    hy: "Հայաստան",
  };

  return label[locale.value as Locale];
};

const galleryItems = computed<string[]>(() => {
  const mainImage = newArt.value?.thumbnail;
  const gallery = newArt.value?.gallery || [];

  // Create a new array to avoid mutating the original artwork.gallery
  return mainImage ? [mainImage, ...gallery] : [...gallery];
});
</script>
